import { Trans } from '@lingui/react/macro';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useBodyScrollLock } from '../lib/scroll-lock.js';
import { CreateBrandForm } from './create-brand-form.js';

// Create-brand modal — triggered from the top-nav brand-switcher's
// "↘ add new brand" link. Lets the founder spin up a new brand from
// wherever they are without yanking them to /brands first. After
// submit the form navigates to /brand/$slug/onboarding/url (the URL-
// scrape step), so the modal's job is just to host the input — close
// happens on success or backdrop/escape.
//
// Backdrop click + Escape both close. The dialog stops mousedown
// propagation so a stray click inside doesn't accidentally close.

export interface CreateBrandModalProps {
  open: boolean;
  onClose: () => void;
}

export function CreateBrandModal({ open, onClose }: CreateBrandModalProps) {
  // Lock page scroll behind the modal.
  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const node = (
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop is intentionally non-interactive for assistive tech; Escape + dialog close cover it
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-[var(--ink)]/35 px-4 py-12 sm:px-6"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-brand-modal-title"
        className="w-full max-w-[560px] rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[6px_6px_0_var(--plum)]"
      >
        <h2 id="create-brand-modal-title" className="sr-only">
          <Trans>new brand</Trans>
        </h2>
        <CreateBrandForm onCreated={() => onClose()} />
      </div>
    </div>
  );

  return createPortal(node, document.body);
}
